import React from 'react';
import { Link } from 'react-router-dom';
import { Share2 } from 'lucide-react';
import { trackEvent } from '../lib/ga';

const API = process.env.REACT_APP_BACKEND_URL;
// QR always points to the homepage (canonical production URL)
const SHARE_URL = 'https://1staveicp.com/';

const Footer = () => {
  const [settings, setSettings] = React.useState(null);

  React.useEffect(() => {
    fetch(`${API}/api/settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSettings(d))
      .catch(() => {});
  }, []);

  const phone = settings?.phone || '(817) 829-4878';
  const email = settings?.email || '1staveicp@gmail.com';
  const address = settings?.address || '103 NE 1st Avenue Mineral Wells, TX 76067';
  const footerImage = settings?.footer_image || '/images/misc/footer-icecream.png';
  const telHref = `tel:${phone.replace(/[^0-9+]/g, '')}`;

  const citiesWeServe = [
    { name: 'Mineral Wells', slug: 'ice-cream-mineral-wells-tx' },
    { name: 'Weatherford', slug: 'ice-cream-weatherford-tx' },
    { name: 'Azle', slug: 'ice-cream-azle-tx' },
    { name: 'Aledo', slug: 'ice-cream-aledo-tx' },
    { name: 'Willow Park', slug: 'ice-cream-willow-park-tx' },
    { name: 'Springtown', slug: 'ice-cream-springtown-tx' },
    { name: 'Graford', slug: 'ice-cream-graford-tx' }
  ];

  return (
    <footer 
      className="py-12 px-4 lg:px-8 relative overflow-visible"
      style={{
        background: 'linear-gradient(135deg, #E91E8C 0%, #C71585 25%, #9B1B6E 50%, #7B1454 75%, #5A0F3D 100%)'
      }}
    >
      {/* Decorative ice cream/cupcake - positioned higher to avoid being blocked */}
      <div 
        className="absolute right-0 md:right-0 w-24 md:w-36 lg:w-44 pointer-events-none"
        style={{
          bottom: '100px',  // Positioned higher to avoid badge
          zIndex: 5
        }}
      >
        <img
          src={footerImage}
          alt=""
          className="w-full h-auto object-contain"
        />
      </div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-4 gap-8 items-start">
          {/* Logo - no border */}
          <div className="flex justify-center md:justify-start">
            <img
              src="/images/logo/logo-circle.png"
              alt="1st Ave ICP Logo"
              className="w-32 h-32"
            />
          </div>

          {/* Get In Touch */}
          <div className="md:col-span-2">
            <h3 
              className="text-2xl font-bold text-white mb-6"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              Get In Touch
            </h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Phone */}
              <div className="flex items-start gap-3">
                <img
                  src="/images/icons/phone.png"
                  alt="Phone"
                  className="w-8 h-8"
                />
                <div>
                  <h4 
                    className="font-bold text-white text-sm mb-1"
                    style={{ fontFamily: 'Rowdies, cursive' }}
                  >
                    Call Now
                  </h4>
                  <a
                    href={telHref}
                    onClick={() => trackEvent('phone_click', { source: 'footer' })}
                    className="text-white hover:text-pink-200 transition-colors text-sm"
                  >
                    {phone}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3" itemScope itemType="https://schema.org/LocalBusiness">
                <a href={`mailto:${email}`}>
                  <img
                    src="/images/icons/email.png"
                    alt="Email"
                    className="w-8 h-8"
                  />
                </a>
                <div>
                  <h4 
                    className="font-bold text-white text-sm mb-1"
                    style={{ fontFamily: 'Rowdies, cursive' }}
                  >
                    <a href={`mailto:${email}`} className="hover:text-pink-200 transition-colors">
                      Email
                    </a>
                  </h4>
                  <a
                    href={`mailto:${email}`}
                    className="text-white hover:text-pink-200 transition-colors text-xs"
                  >
                    {email}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-3" itemScope itemType="https://schema.org/LocalBusiness">
                <a 
                  href="https://www.google.com/maps/place/103+NE+1st+Ave,+Mineral+Wells,+TX+76067"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('directions_click', { source: 'footer_map_icon' })}
                >
                  <img
                    src="/images/icons/map.png"
                    alt="Get Directions to 1st Ave ICP"
                    className="w-8 h-8"
                  />
                </a>
                <div>
                  <h4 
                    className="font-bold text-white text-sm mb-1"
                    style={{ fontFamily: 'Rowdies, cursive' }}
                  >
                    <a 
                      href="https://www.google.com/maps/place/103+NE+1st+Ave,+Mineral+Wells,+TX+76067"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('directions_click', { source: 'footer_address' })}
                      className="hover:text-pink-200 transition-colors"
                    >
                      Directions
                    </a>
                  </h4>
                  <address className="text-white text-xs not-italic" itemProp="address">
                    {address}
                  </address>
                </div>
              </div>
            </div>

            {/* Share This Page — QR nested in left column to fill the void */}
            <div className="mt-8">
              <h3
                className="text-xl font-bold text-white mb-1 flex items-center gap-2"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                <Share2 className="w-5 h-5" />
                Share with a Friend — From Your Phone
              </h3>
              <p className="text-pink-100 text-sm mb-4">
                Scan QR Code to theirs
              </p>
              <div
                className="bg-white p-3 rounded-2xl shadow-xl inline-block"
                data-testid="footer-qr-share"
              >
                <img
                  src={`${API}/api/qr-share?url=${encodeURIComponent(SHARE_URL)}&fmt=svg`}
                  alt="Scan to visit 1stAveICP.com"
                  className="w-36 h-36 md:w-40 md:h-40 block"
                />
              </div>
            </div>
          </div>

          {/* Cities We Serve */}
          <div>
            <h3 
              className="text-2xl font-bold text-white mb-6"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              Quick Links
            </h3>
            <ul className="space-y-2 mb-6">
              <li><Link to="/menu" className="text-pink-200 hover:text-white transition-colors text-sm">Menu</Link></li>
              <li><Link to="/specials" className="text-pink-200 hover:text-white transition-colors text-sm">Specials & Deals</Link></li>
              <li><Link to="/book-event" className="text-pink-200 hover:text-white transition-colors text-sm">Book an Event</Link></li>
              <li><Link to="/find-us" className="text-pink-200 hover:text-white transition-colors text-sm">Find Us / Hours</Link></li>
              <li><Link to="/reviews" className="text-pink-200 hover:text-white transition-colors text-sm">Customer Reviews</Link></li>
              <li><Link to="/winners" className="text-pink-200 hover:text-white transition-colors text-sm">Weekly Winners</Link></li>
            </ul>
            <h3 
              className="text-2xl font-bold text-white mb-4"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              Cities We Serve
            </h3>
            <ul className="space-y-2">
              {citiesWeServe.map(city => (
                <li key={city.slug}>
                  <Link
                    to={`/${city.slug}`}
                    className="text-pink-200 hover:text-white transition-colors text-sm flex items-center gap-2"
                  >
                    <span className="text-xs">📍</span>
                    {city.name}, TX
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Review Us Section */}
        <div className="mt-8 pt-6 border-t border-pink-400/30">
          <h3 
            className="text-xl font-bold text-white mb-4 text-center"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Love Our Ice Cream? Leave Us a Review!
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.google.com/maps/place/1st+Avenue+ICP/@32.8095446,-98.1122545,17z/data=!4m8!3m7!1s0x8651f5ef009d6f9f:0x8537476ef96c05cb!8m2!3d32.8095446!4d-98.1122545!9m1!1b1!16s%2Fg%2F11ynz66hbg"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-all hover:scale-105"
              data-testid="review-google-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-white font-semibold text-sm">Google</span>
            </a>
            <a
              href="https://www.yelp.com/biz/1st-avenue-icp-mineral-wells?osq=1st+avenue+icp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-all hover:scale-105"
              data-testid="review-yelp-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#FF1A1A">
                <path d="M20.16 12.594l-4.995 1.433c-.96.276-1.74-.8-1.176-1.63l2.905-4.308a1.072 1.072 0 0 1 1.596-.206 9.194 9.194 0 0 1 2.364 3.252 1.073 1.073 0 0 1-.694 1.459zm-3.965 5.985a1.072 1.072 0 0 1-.94-.38 9.194 9.194 0 0 1-1.573-3.653 1.073 1.073 0 0 1 .865-1.283l5.126-.922c1.001-.18 1.676.996 1.02 1.78l-3.378 4.037c-.22.264-.571.42-.94.42h-.18zm-5.08-4.128l-4.15 3.118c-.827.62-1.895-.202-1.615-1.244l1.47-5.075a1.072 1.072 0 0 1 1.477-.684c1.2.536 2.25 1.342 3.056 2.34.487.604.345 1.482-.238 1.865zm.296-4.466l1.83-4.883c.37-.98 1.738-.98 2.068.02l1.71 4.903c.3.86-.55 1.69-1.33 1.3l-3.54-1.77c-.75-.375-.987-1.238-.738-1.57zm-6.28 10.93a1.072 1.072 0 0 1-.32-1.49l2.762-4.4c.55-.876 1.884-.56 2.015.478l.67 5.256c.13 1.015-1.002 1.7-1.828 1.1l-3.3-2.944z"/>
              </svg>
              <span className="text-white font-semibold text-sm">Yelp</span>
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61559570946841&sk=reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-all hover:scale-105"
              data-testid="review-facebook-btn"
            >
              <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-white font-semibold text-sm">Facebook</span>
            </a>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="mt-10 pt-6 border-t border-pink-400/50 text-center">
          <p className="text-white text-sm mb-2">
            Web Design by{' '}
            <a
              href="https://web-techservices.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-200 hover:text-white underline font-semibold"
            >
              Web-TechServices.com
            </a>
            {'  '}© {new Date().getFullYear()} 1stAveicp
            {'  '}
            <a
              href="/admin/login"
              className="text-pink-300 hover:text-white text-xs ml-4 opacity-60 hover:opacity-100 transition-opacity"
            >
              Admin
            </a>
          </p>
          <div className="flex justify-center gap-4 text-xs mt-2">
            <a
              href="/privacy-policy"
              className="text-pink-200 hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-pink-400">|</span>
            <a
              href="/sms-terms"
              className="text-pink-200 hover:text-white transition-colors"
            >
              SMS Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
